import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type Period,
  currentPeriod,
  parsePeriod,
  toPeriodString,
  monthBounds,
  periodFromDate,
  fromDateString,
  toDateString,
} from '@/lib/format';

export type FilterMode = 'month' | 'range';
export type FilterType = 'all' | 'income' | 'expense';

export interface FiltersState {
  mode: FilterMode;
  period: Period;
  from: Date;
  to: Date;
  type: FilterType;
  category: string;
}

export interface FiltersOptions {
  /** When true (Transações), pending also tracks type and category. */
  includeTransactionFilters?: boolean;
  /**
   * When true, the period is always one calendar month: `from`/`to` in the URL are ignored,
   * and a link that carries a range lands on the month its `from` falls in. (No page uses it today —
   * Painel keeps the range and has its budget section say it is monthly.)
   */
  monthOnly?: boolean;
}

const TYPE_VALUES: ReadonlySet<FilterType> = new Set(['all', 'income', 'expense']);

const decodeFilters = (params: URLSearchParams, monthOnly: boolean): FiltersState => {
  const periodParam = parsePeriod(params.get('period'));
  const fromParam = fromDateString(params.get('from') ?? '');
  const toParam = fromDateString(params.get('to') ?? '');
  const typeParam = params.get('type');
  const categoryParam = params.get('category');

  if (!monthOnly && fromParam && toParam) {
    return {
      mode: 'range',
      period: periodFromDate(fromParam),
      from: fromParam,
      to: toParam,
      type: TYPE_VALUES.has(typeParam as FilterType) ? (typeParam as FilterType) : 'all',
      category: categoryParam ?? 'all',
    };
  }

  const period = periodParam ?? (monthOnly && fromParam ? periodFromDate(fromParam) : null) ?? currentPeriod();
  const { from, to } = monthBounds(period);
  return {
    mode: 'month',
    period,
    from,
    to,
    type: TYPE_VALUES.has(typeParam as FilterType) ? (typeParam as FilterType) : 'all',
    category: categoryParam ?? 'all',
  };
};

const encodeFilters = (
  state: FiltersState,
  preserve: URLSearchParams,
  includeTransactionFilters: boolean,
): URLSearchParams => {
  const next = new URLSearchParams(preserve);
  next.delete('period');
  next.delete('from');
  next.delete('to');

  if (state.mode === 'range') {
    next.set('from', toDateString(state.from));
    next.set('to', toDateString(state.to));
  } else {
    next.set('period', toPeriodString(state.period));
  }

  if (includeTransactionFilters) {
    next.delete('type');
    next.delete('category');
    if (state.type !== 'all') next.set('type', state.type);
    if (state.category !== 'all') next.set('category', state.category);
  }

  return next;
};

const sameState = (a: FiltersState, b: FiltersState): boolean =>
  a.mode === b.mode &&
  a.from.getTime() === b.from.getTime() &&
  a.to.getTime() === b.to.getTime() &&
  a.type === b.type &&
  a.category === b.category;

export interface UseFiltersResult {
  applied: FiltersState;
  pending: FiltersState;
  isDirty: boolean;
  setPending: (updater: (prev: FiltersState) => FiltersState) => void;
  setMonth: (period: Period) => void;
  setRange: (from: Date, to: Date) => void;
  setMode: (mode: FilterMode) => void;
  setType: (type: FilterType) => void;
  setCategory: (category: string) => void;
  /** Commit a month-mode period to the URL immediately. Preserves applied type/category. */
  applyMonth: (period: Period) => void;
  /** Reset to defaults AND commit immediately. */
  applyReset: () => void;
  apply: () => void;
  reset: () => void;
}

export const useFilters = (options: FiltersOptions = {}): UseFiltersResult => {
  const { includeTransactionFilters = false, monthOnly = false } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const applied = useMemo(() => decodeFilters(searchParams, monthOnly), [searchParams, monthOnly]);
  const [pending, setPendingState] = useState<FiltersState>(applied);
  const [syncedApplied, setSyncedApplied] = useState<FiltersState>(applied);

  // `pending` follows `applied` — a link, Back/Forward — but only when what `applied` means has changed: a param
  // this hook doesn't own is no reason to drop a draft. Done while rendering, not in an effect: React restarts the
  // render with the new state before committing, so there is no frame where `pending` lags behind `applied` and
  // `isDirty` flashes Aplicar and Limpar.
  if (!sameState(syncedApplied, applied)) {
    setSyncedApplied(applied);
    setPendingState(applied);
  }

  const setPending: UseFiltersResult['setPending'] = useCallback((updater) => {
    setPendingState((prev) => updater(prev));
  }, []);

  const setMonth = useCallback((period: Period) => {
    setPendingState((prev) => {
      const { from, to } = monthBounds(period);
      return { ...prev, mode: 'month', period, from, to };
    });
  }, []);

  const setRange = useCallback((from: Date, to: Date) => {
    setPendingState((prev) => ({
      ...prev,
      mode: 'range',
      period: periodFromDate(from),
      from,
      to,
    }));
  }, []);

  const setMode = useCallback((mode: FilterMode) => {
    setPendingState((prev) => {
      if (prev.mode === mode) return prev;
      if (mode === 'month') {
        const { from, to } = monthBounds(prev.period);
        return { ...prev, mode: 'month', from, to };
      }
      return { ...prev, mode: 'range' };
    });
  }, []);

  const setType = useCallback((type: FilterType) => {
    setPendingState((prev) => ({ ...prev, type }));
  }, []);

  const setCategory = useCallback((category: string) => {
    setPendingState((prev) => ({ ...prev, category }));
  }, []);

  const apply = useCallback(() => {
    setSearchParams(
      (prev) => encodeFilters(pending, prev, includeTransactionFilters),
      { replace: false },
    );
  }, [pending, setSearchParams, includeTransactionFilters]);

  const applyMonth = useCallback(
    (period: Period) => {
      const { from, to } = monthBounds(period);
      setPendingState((prev) => ({ ...prev, mode: 'month', period, from, to }));
      setSearchParams(
        (prev) => {
          const next: FiltersState = {
            ...applied,
            mode: 'month',
            period,
            from,
            to,
          };
          return encodeFilters(next, prev, includeTransactionFilters);
        },
        { replace: false },
      );
    },
    [applied, setSearchParams, includeTransactionFilters],
  );

  const reset = useCallback(() => {
    const period = currentPeriod();
    const { from, to } = monthBounds(period);
    setPendingState({
      mode: 'month',
      period,
      from,
      to,
      type: 'all',
      category: 'all',
    });
  }, []);

  const applyReset = useCallback(() => {
    const period = currentPeriod();
    const { from, to } = monthBounds(period);
    const defaults: FiltersState = {
      mode: 'month',
      period,
      from,
      to,
      type: 'all',
      category: 'all',
    };
    setPendingState(defaults);
    setSearchParams(
      (prev) => encodeFilters(defaults, prev, includeTransactionFilters),
      { replace: false },
    );
  }, [setSearchParams, includeTransactionFilters]);

  const isDirty = useMemo(() => !sameState(applied, pending), [applied, pending]);

  return {
    applied,
    pending,
    isDirty,
    setPending,
    setMonth,
    setRange,
    setMode,
    setType,
    setCategory,
    applyMonth,
    applyReset,
    apply,
    reset,
  };
};

/**
 * Build a search-string for cross-page links that should carry the current applied filters.
 * Includes only the keys the destination page understands.
 */
export const filtersToSearchString = (
  state: FiltersState,
  options: { includeTransactionFilters?: boolean } = {},
): string => {
  const params = new URLSearchParams();
  if (state.mode === 'range') {
    params.set('from', toDateString(state.from));
    params.set('to', toDateString(state.to));
  } else {
    params.set('period', toPeriodString(state.period));
  }
  if (options.includeTransactionFilters) {
    if (state.type !== 'all') params.set('type', state.type);
    if (state.category !== 'all') params.set('category', state.category);
  }
  const str = params.toString();
  return str ? `?${str}` : '';
};
